
import { useAudioPlayerStore } from "~/stores/audioplayer";
import { useRuntimeConfig } from '#imports';

export async function fetchTrack(trackId, idType = 'track') {
  if (!trackId) {
    console.warn('[SoundCloud] Missing trackId');
    return null;
  }

  try {
    const config = useRuntimeConfig();
    // Use WordPress proxy endpoint that handles authentication
    // This solves the 401 issue - stream URLs require OAuth headers that browsers can't send
    // TEMPORARY STOPGAP (2026-08-12): SoundCloud switched off http_mp3_128_url, which
    // format=mp3 maps to, so every track 404s. preview is capped at ~30s and is a
    // regression against 5e4f0c230 - replace with the AAC/HLS fallback chain.
    const proxyUrl = `${config.public.baseUrl}/wp-json/content-search/v1/audio-proxy/${trackId}?format=preview&id_type=${idType}`;
    return proxyUrl;
  } catch (e) {
    console.error('[SoundCloud] Error creating proxy URL:', {
      error: e.message,
      trackId,
      stack: e.stack
    });
    return null
  }
}

// Identity of a playable track: the CDN demo at index (default first), else SoundCloud id.
// With the default index it identifies the product, which is what play buttons compare.
export function getAudioKey(details, index = 0) {
  return details?.audioDemos?.[index]?.url || details?.soundcloudId || undefined
}

// Resolve the playable source. CDN demo if present, otherwise the SoundCloud proxy URL
export async function resolveAudioSource(productDetails, getSoundcloudUrl, index = 0) {
  const demo = productDetails?.audioDemos?.[index]
  if (!demo?.url) {
    const url = await getSoundcloudUrl()
    return url ? { url, peaks: null, duration: null, source: 'soundcloud' } : null
  }

  let peaks = null
  if (demo.peaksUrl) {
    try {
      const data = await $fetch(demo.peaksUrl)
      if (Array.isArray(data?.peaks) && data.peaks.length >= 1 && data.peaks.length <= 4000) {
        peaks = [data.peaks.map((v) => {
          const n = Number(v)
          return Number.isFinite(n) ? Math.min(1, Math.max(-1, n)) : 0
        })]
      }
    } catch (e) {
      console.warn('[AudioPlayer] Peaks unavailable, falling back to decode:', e?.message)
    }
  }
  return {
    url: demo.url,
    peaks,
    duration: Number(demo.duration) || null,
    source: 'cdn',
    lufs: demo.lufs ?? null,
    truePeak: demo.truePeak ?? null,
  }
}

export async function fetchAudioToken() {
  const audioPlayerStore = useAudioPlayerStore();
  const config = useRuntimeConfig();
  const { audioPlayerToken } = await $fetch(`${config.public.baseUrl}/wp-json/content-search/v1/search?type=audio-token`, {
    method: 'GET',
  });

  if (audioPlayerToken) {
    audioPlayerStore.setAudioPlayerToken(audioPlayerToken)
  }

  return audioPlayerToken
}

export function formatTime(seconds) {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)

  // If hours are 0, omit it from the format, otherwise, pad it to 1+ digits
  const formattedHours = h > 0 ? `${h}:` : ''

  // Pad minutes and seconds with leading zeros if needed
  const formattedMinutes = h > 0
    ? m.toString().padStart(2, '0')
    : m
  const formattedSeconds = s.toString().padStart(2, '0');

  return `${formattedHours}${formattedMinutes}:${formattedSeconds}`
}